import { makeAutoObservable, runInAction } from 'mobx';
import {
  adminShiftTemplatesCreate,
  adminShiftTemplatesDelete,
  adminShiftTemplatesGetList,
  adminShiftTemplatesGetOne,
  adminShiftTemplatesUpdate,
} from '@/services/shiftTemplatesApi';
import { getApiErrorMessage } from '@/utils/errors';
import type {
  CreateShiftTemplateDto,
  ShiftTemplate,
  UpdateShiftTemplateDto,
} from '@/types/shift.types';
import { toastStore } from './ToastStore';

class ShiftTemplatesStore {
  templates: ShiftTemplate[] = [];
  templatesLoading = false;
  templatesError: string | null = null;

  activeTemplate: ShiftTemplate | null = null;
  isLoadingTemplate = false;
  isSaving = false;
  isDeleting = false;
  submitError: string | null = null;

  private serviceCenterUuid: string | null = null;
  private templatesLoadedFor: string | null = null;
  private listRequestId = 0;
  private detailRequestId = 0;

  constructor() {
    makeAutoObservable(this);
  }

  setServiceCenterUuid = (serviceCenterUuid: string) => {
    if (this.serviceCenterUuid === serviceCenterUuid) return;
    this.serviceCenterUuid = serviceCenterUuid;
    void this.loadTemplates(serviceCenterUuid);
  };

  loadTemplates = async (serviceCenterUuid: string, options?: { force?: boolean }) => {
    if (!serviceCenterUuid) return;
    if (!options?.force && this.templatesLoadedFor === serviceCenterUuid) return;

    this.serviceCenterUuid = serviceCenterUuid;
    const requestId = ++this.listRequestId;
    const previousTemplates = this.templates;
    const previousLoadedFor = this.templatesLoadedFor;
    this.templatesLoading = true;
    this.templatesError = null;

    try {
      const response = await adminShiftTemplatesGetList(serviceCenterUuid);
      if (requestId !== this.listRequestId) return;
      runInAction(() => {
        this.templates = response.data ?? [];
        this.templatesLoadedFor = serviceCenterUuid;
        this.templatesLoading = false;
        this.templatesError = null;
      });
    } catch (error) {
      if (requestId !== this.listRequestId) return;
      const message = getApiErrorMessage(error, 'Не удалось загрузить шаблоны смен');
      runInAction(() => {
        if (options?.force && previousTemplates.length > 0) {
          this.templates = previousTemplates;
          this.templatesLoadedFor = previousLoadedFor;
        } else {
          this.templates = [];
          this.templatesLoadedFor = null;
        }
        this.templatesLoading = false;
        this.templatesError = message;
      });
      toastStore.showError(message);
    }
  };

  loadTemplateDetail = async (uuid: string) => {
    const requestId = ++this.detailRequestId;
    this.isLoadingTemplate = true;
    this.activeTemplate = null;
    this.submitError = null;
    try {
      const template = await adminShiftTemplatesGetOne(uuid);
      if (requestId !== this.detailRequestId) return;
      runInAction(() => {
        this.activeTemplate = template;
        this.isLoadingTemplate = false;
      });
    } catch (error) {
      if (requestId !== this.detailRequestId) return;
      const message = getApiErrorMessage(error, 'Не удалось загрузить шаблон смены');
      runInAction(() => {
        this.activeTemplate = null;
        this.isLoadingTemplate = false;
        this.submitError = message;
      });
      toastStore.showError(message);
    }
  };

  clearActiveTemplate = () => {
    this.detailRequestId += 1;
    this.activeTemplate = null;
    this.submitError = null;
    this.isLoadingTemplate = false;
  };

  createTemplate = async (
    payload: CreateShiftTemplateDto,
  ): Promise<ShiftTemplate | null> => {
    this.isSaving = true;
    this.submitError = null;
    try {
      const created = await adminShiftTemplatesCreate(payload);
      runInAction(() => {
        this.activeTemplate = created;
        this.upsertTemplateInList(created);
      });
      toastStore.showSuccess('Шаблон создан');
      this.refreshTemplatesList();
      return created;
    } catch (error) {
      const message = getApiErrorMessage(error, 'Не удалось сохранить шаблон');
      runInAction(() => {
        this.submitError = message;
      });
      toastStore.showError(message);
      return null;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  updateTemplate = async (
    uuid: string,
    payload: UpdateShiftTemplateDto,
  ): Promise<ShiftTemplate | null> => {
    this.isSaving = true;
    this.submitError = null;
    try {
      const updated = await adminShiftTemplatesUpdate(uuid, payload);
      runInAction(() => {
        this.activeTemplate = updated;
        this.upsertTemplateInList(updated);
      });
      toastStore.showSuccess('Шаблон сохранён');
      this.refreshTemplatesList();
      return updated;
    } catch (error) {
      const message = getApiErrorMessage(error, 'Не удалось сохранить шаблон');
      runInAction(() => {
        this.submitError = message;
      });
      toastStore.showError(message);
      return null;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  deleteTemplate = async (uuid: string): Promise<boolean> => {
    this.isDeleting = true;
    this.submitError = null;
    try {
      await adminShiftTemplatesDelete(uuid);
      runInAction(() => {
        this.activeTemplate = null;
        this.removeTemplateFromList(uuid);
      });
      toastStore.showSuccess('Шаблон удалён');
      this.refreshTemplatesList();
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error, 'Не удалось удалить шаблон');
      runInAction(() => {
        this.submitError = message;
      });
      toastStore.showError(message);
      return false;
    } finally {
      runInAction(() => {
        this.isDeleting = false;
      });
    }
  };

  clear = () => {
    this.listRequestId += 1;
    this.detailRequestId += 1;
    this.templates = [];
    this.templatesLoading = false;
    this.templatesError = null;
    this.templatesLoadedFor = null;
    this.activeTemplate = null;
    this.isLoadingTemplate = false;
    this.isSaving = false;
    this.isDeleting = false;
    this.submitError = null;
    this.serviceCenterUuid = null;
  };

  private upsertTemplateInList = (template: ShiftTemplate) => {
    const index = this.templates.findIndex((item) => item.uuid === template.uuid);
    if (index >= 0) {
      this.templates = this.templates.map((item, i) => (i === index ? template : item));
      return;
    }
    this.templates = [...this.templates, template];
  };

  private removeTemplateFromList = (uuid: string) => {
    this.templates = this.templates.filter((item) => item.uuid !== uuid);
  };

  private refreshTemplatesList = () => {
    if (!this.serviceCenterUuid) return;
    void this.loadTemplates(this.serviceCenterUuid, { force: true });
  };
}

export const shiftTemplatesStore = new ShiftTemplatesStore();
export type { ShiftTemplatesStore };
